import { Box, VStack } from '@cdl/heartbeat-react';
import { debounce } from 'lodash';
import {
  UIEvent,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { EmptyContent } from '../../../../shared/components/EmptyContent';
import { filterOptionSelector } from '../../../layout/selectors/layout.selectors';
import {
  baseClustersSelector,
  clusterHorizontalLengthSelector,
  clusterVerticalRangeSelector,
} from '../../selectors/cluster.selectors';
import {
  emptyMessageMorphologySelector,
  filteredMorphologiesSelector,
} from '../../selectors/morphology.selectors';
import { WebGLContainer } from '../../../plot/components/WebGLContainer';
import { MorphologiesHeader } from './MorphologiesHeader';
import { Morphology, MORPHOLOGY_HEIGHT, MORPHOLOGY_WIDTH } from './Morphology';
import {
  dimensionReducer,
  measureDimension,
} from '../../../plot/utils/web-gl.util';
import { FilterOption } from '../../../layout/types/layout-state.type';
import { Cluster } from '../../clustering.types';

const CONTAINER_HEIGHT = 0.66 * window.innerHeight;
const CONTAINER_MARGIN = 20;
const CONTAINER_WIDTH = window.innerWidth - CONTAINER_MARGIN;
const HEAD_BAR_HEIGHT = 60;

export const MorphologiesContainer: React.FC = () => {
  const morphologies = useSelector(filteredMorphologiesSelector);
  const baseClusters = useSelector(baseClustersSelector);
  const [element, setElement] = useState<HTMLElement | null>(null);
  const [scrollElement, setScrollElement] = useState<HTMLElement | null>(null);
  const [height, setHeight] = useState<number>(CONTAINER_HEIGHT);
  const message = useSelector(emptyMessageMorphologySelector);
  const filterOption = useSelector(filterOptionSelector);
  const xMax = useSelector(
    clusterHorizontalLengthSelector(morphologies[0]?.id),
  );
  const { yMin, yMax } = useSelector(clusterVerticalRangeSelector);
  const [dimensions, dispatchToReducer] = useReducer(dimensionReducer, {});
  const [verticalScroll, setVerticalScroll] = useState(0);
  const [oldFilter, setOldFilter] = useState<FilterOption>('All');
  const [oldMorphologies, setOldMorphologies] = useState<Cluster[]>([]);

  useEffect(() => {
    const onResize = () => {
      if (element == null) {
        return;
      }

      setHeight(window.innerHeight - element.offsetTop);
    };
    onResize();
    window.addEventListener('resize', debounce(onResize, 150));
    return () => window.removeEventListener('resize', onResize);
  }, [element, morphologies, baseClusters]);

  const onScroll = useCallback(
    (event: UIEvent<HTMLElement, globalThis.UIEvent>) => {
      setVerticalScroll(event.currentTarget.scrollTop);
    },
    [],
  );

  const MemoisedMorphologies = useMemo(() => {
    // initialize the dimensions because a morphology
    // has been added / deleted so we need their new positions
    // in the screen
    dispatchToReducer({ type: 'initialize' });
    let scroll = verticalScroll;
    if (
      oldMorphologies.length > morphologies.length &&
      filterOption === oldFilter &&
      scrollElement != null
    ) {
      const maxScroll = scrollElement.scrollHeight - scrollElement.clientHeight;
      if (verticalScroll > maxScroll - CONTAINER_HEIGHT) {
        scroll =
          maxScroll - CONTAINER_HEIGHT > 0 ? maxScroll - CONTAINER_HEIGHT : 0;
        scrollElement.scrollTop = scroll;
        setVerticalScroll(scroll);
      }
    }
    setOldMorphologies(morphologies);
    if (filterOption !== oldFilter && scrollElement != null) {
      scroll = 0;
      scrollElement.scrollTop = scroll;
      setVerticalScroll(scroll);
      setOldFilter(filterOption);
    }

    return morphologies.map((morphology) => (
      <Morphology
        key={morphology.id}
        morphology={morphology}
        ref={(node) =>
          measureDimension(
            node,
            morphology,
            xMax,
            { x: 0, y: -scroll },
            dispatchToReducer,
          )
        }
      />
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [morphologies, xMax, baseClusters]);

  return (
    <VStack
      spacing='s'
      alignItems='start'
      mt='l'
      px='m'
      height={height}
      ref={setElement}
    >
      <MorphologiesHeader morphologyNumber={morphologies.length} />
      {filterOption === 'Noise' ? (
        <EmptyContent message={message} width='100%' height='100%' />
      ) : (
        <Box
          width='100%'
          overflow='auto'
          onScroll={onScroll}
          height={height}
          ref={setScrollElement}
        >
          <WebGLContainer
            width={MORPHOLOGY_WIDTH}
            height={MORPHOLOGY_HEIGHT}
            canvasWidth={CONTAINER_WIDTH}
            canvasHeight={height - HEAD_BAR_HEIGHT}
            clusters={morphologies}
            style={{
              position: 'absolute',
            }}
            xAxis={{ xMax }}
            yAxis={{ yMin, yMax }}
            scroll={{ x: 0, y: -verticalScroll }}
            dimensions={dimensions}
          />
          <Box display='flex' flexWrap='wrap'>
            {MemoisedMorphologies}
          </Box>
        </Box>
      )}
    </VStack>
  );
};
